import { Link, useParams } from 'react-router-dom';

import { GetSubsetOverviewResponseDto } from '../../../../../../../../../backend/src/matter/subset/dto/get-subset-overview.response.dto';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { GetCaseOverviewRes } from '@/api/types/queries';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/shadcn/skeleton';

const KeyDocumentsSectionLoader = () => {
  return (
    <div className="space-y-2">
      {[...Array(3)].map((_, index) => (
        <div key={index} className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 p-2">
          <Skeleton className="h-3 w-32 bg-gray-200" />
          <div className="ml-auto flex items-center gap-1">
            <Skeleton className="h-3 w-4 bg-gray-200" />
            <Skeleton className="h-3 w-8 bg-gray-200" />
          </div>
        </div>
      ))}
    </div>
  );
};

interface KeyDocumentsSectionProps {
  overview: GetSubsetOverviewResponseDto | undefined;
  overviewIsLoading: boolean;
}

export const KeyDocumentsSection = ({ overview, overviewIsLoading }: KeyDocumentsSectionProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  if (!overviewIsLoading && !overview?.keyFactsByDoc?.length) {
    return null;
  }

  return (
    <div className="h-full basis-1/2">
      <div className="flex h-full flex-col rounded-md border border-gray-200 bg-white">
        <div className="flex items-center justify-between rounded-t-md border-b bg-gray-50 p-3 px-4">
          <div className="flex items-center gap-3">
            <Icon className="lucide--file-text h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-700">Key Documents</h3>
          </div>
          <Button asChild variant="ghost" className="p-0 text-xs text-blue-500 underline hover:text-blue-700" size="sm">
            <Link to={`/matters/${matterId}/subsets/${subsetId}/docs`}>
              View Subset Documents
              <Icon className="lucide--external-link h-3 w-3" />
            </Link>
          </Button>
        </div>

        <div className="flex-1 p-4">
          {overviewIsLoading ? (
            <KeyDocumentsSectionLoader />
          ) : (
            overview?.keyFactsByDoc?.length && (
              <div className="space-y-2">
                {overview.keyFactsByDoc.slice(0, 3).map((doc, index) => (
                  <Link
                    key={doc.documentId}
                    to={`/matters/${matterId}/subsets/${subsetId}/docs/${doc.documentId}`}
                    onClick={() => trackCustomEvent(TrackingEventsV2.ViewKeyDocumentsFromSubsetSummary)}
                    className={`flex cursor-pointer items-center justify-between gap-4 rounded-md border border-gray-200 bg-gray-50 p-2 px-2.5 text-xs transition-all hover:border-gray-300 hover:bg-gray-100`}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Icon className="lucide--file-text h-3 w-3 flex-shrink-0 text-gray-400" />
                      <span className="truncate text-gray-700 italic" title={doc.fileName}>
                        {doc.fileName}
                      </span>
                    </div>
                    <div className="ml-2 flex items-center gap-2 text-gray-600">
                      <strong>{doc.count}</strong> key facts
                      <Icon className="lucide--key-round h-3 w-3 flex-shrink-0 text-gray-400" />
                    </div>
                  </Link>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};
